import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FileText, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf, downloadBulkCertificatesPdf } from "@/lib/pdf";

const SEMESTER_LABELS = { 1: "الفصل الدراسي الأول", 2: "الفصل الدراسي الثاني" };
const MODE_LABELS = { "year-work": "أعمال السنة", "final-exam": "الاختبار النهائي" };

/**
 * GradeSheetPrint — طباعة كشف رصد لمكوّن واحد خام كما أُدخل (أعمال
 * السنة أو الاختبار النهائي فقط) — منفصل تمامًا عن كشف "مراجعة رصد
 * الدرجات" الذي يعرض الدرجة النهائية المُحوَّلة. يدعم كشفًا فارغًا
 * للرصد اليدوي أو كشفًا بالدرجات المُدخَلة فعليًا، ولطباعة كل مواد
 * صف/فصل معًا في ملف واحد مُجمَّع.
 */
export default function GradeSheetPrint() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [semester, setSemester] = useState(1);
  const [mode, setMode] = useState("year-work");
  const [fillType, setFillType] = useState("blank"); // blank | filled
  const [grade, setGrade] = useState("");
  const [cls, setCls] = useState("");
  const [subjectId, setSubjectId] = useState("__all__");
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!grade) { setClasses([]); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, grade]);

  useEffect(() => {
    if (!grade) { setSubjects([]); return; }
    api.get("/subjects", { params: { stage: ctx.stage, year: ctx.year, grade, semester } })
      .then(({ data }) => setSubjects(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [ctx.stage, ctx.year, grade, semester]);

  const targetSubjects = useMemo(() => {
    if (subjectId === "__all__") return subjects;
    return subjects.filter((s) => s.id === subjectId);
  }, [subjects, subjectId]);

  const generate = async () => {
    if (!grade || !cls) return toast.error("اختر الصف والفصل أولاً");
    if (!targetSubjects.length) return toast.error("لا توجد مواد لهذا التحديد");
    setGenerating(true);
    try {
      const isAllClasses = cls === "__all__";
      // "class" يحدِّد فقط قائمة الطلاب المعروضة هنا (classParam أدناه
      // تُستخدَم فقط مع /students). جلب الدرجات نفسها (لاحقًا في هذه
      // الدالة) يتم دائمًا بدون فلترة class والربط بـ student_id فقط —
      // لأن حقل "class" المخزَّن داخل مستند الدرجة قد يكون الفصل القديم
      // للطالب وقت آخر حفظ، ولا يتزامن تلقائيًا عند نقله لفصل جديد.
      const classParam = isAllClasses ? {} : { class_name: cls };
      const { data: students } = await api.get("/students", {
        params: { stage: ctx.stage, year: ctx.year, grade, ...classParam },
      });
      // مرتّب برقم الجلوس تصاعديًا دائمًا — بما في ذلك عند اختيار "كل
      // الفصول" معًا، حيث يُدمَج كل طلاب الصف في كشف واحد مرتّب برقم
      // الجلوس بلا اعتبار لأي طالب من أي فصل تحديدًا.
      const sorted = [...students].sort((a, b) =>
        (parseInt(a.seat_number, 10) || 0) - (parseInt(b.seat_number, 10) || 0));

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const { createRoot } = await import("react-dom/client");
      const nodes = [];
      const A4_HEIGHT_PX = 1123; // نفس أبعاد الشهادات بالضبط (794×1123 عند 96 نقطة/بوصة)

      for (const subj of targetSubjects) {
        let valueMap = {};
        let components = [];
        if (mode === "year-work") {
          // نجيب قائمة مكوّنات أعمال السنة (اسم كل مكوّن + درجته العظمى)
          // بنفس ترتيبها في شاشة الرصد نفسها — بدل عرض رقم إجمالي واحد فقط.
          const { data: compData } = await api.get("/subject-components", {
            params: {
              stage: ctx.stage, year: ctx.year, semester,
              subject_name: subj.subject_name, section: subj.section, grade,
            },
          });
          components = compData.components || [];
          if (fillType === "filled") {
            const { data: existing } = await api.get("/grades/year-work", {
              params: {
                stage: ctx.stage, year: ctx.year, grade,
                subject_name: subj.subject_name, semester, section: subj.section,
              },
            });
            existing.forEach((g) => { valueMap[g.student_id] = g.additional_data || {}; });
          }
        } else if (fillType === "filled") {
          const { data: existing } = await api.get("/grades/final-exam", {
            params: {
              stage: ctx.stage, year: ctx.year, grade,
              subject_name: subj.subject_name, semester, section: subj.section,
            },
          });
          existing.forEach((g) => { valueMap[g.student_id] = g.grade_value; });
        }
        const maxScore = mode === "year-work" ? subj.year_work_score : subj.final_exam_score;

        // السبب الحقيقي لاختفاء صفوف بعينها عند حدود بعض الصفحات (وليس
        // كلها): كنت أقيس "عدد ثابت من الصفوف يتّسع في صفحة واحدة" مرة
        // واحدة من بداية القائمة، ثم أُطبِّق هذا العدد الثابت نفسه على كل
        // صفحة تالية بالتساوي (slice بحجم ثابت). لكن ارتفاع كل صف ليس
        // موحَّدًا فعليًا: بعض أسماء الطلاب طويلة وتلتفّ لسطرين، فتصبح تلك
        // الصفوف أطول من غيرها. مجموعة طلاب معيّنة (نطاق صفحة بعينها) قد
        // تحتوي عددًا أكبر من الأسماء الطويلة الملتفّة مقارنة بالمجموعة
        // التي قِسنا عليها العدد الثابت أول مرة — فتتجاوز تلك الصفحة تحديدًا
        // ارتفاع A4 الفعلي رغم أنها بنفس "العدد"، ويُقصّ آخر صف فيها بصمت
        // (overflow: hidden). هذا فسّر اختفاء صف 36 وصف 54 تحديدًا (آخر صف
        // في صفحتيهما) دون غيرهما.
        //
        // الحل الجذري: نقيس ارتفاع **كل صف على حدة** (وليس رقمًا واحدًا
        // يُطبَّق على الجميع)، ثم نوزّع الطلاب على الصفحات بخوارزمية تعبئة
        // تراكمية حقيقية — كل صفحة تمتلئ بأكبر عدد من الطلاب المتتاليين
        // يتّسعون فعليًا ضمن نفس ميزانية الارتفاع المتاحة (تحسب من جديد
        // لكل صفحة، لأن الترويسة والتذييل يتكرران بنفس الحجم في كل صفحة) —
        // فمهما اختلف عدد الأسماء الطويلة من نطاق لآخر، تبقى النتيجة دقيقة
        // لكل صفحة على حدة، ومستحيل أن يتجاوز أي صف حدود صفحته.
        const probeStudents = sorted.length ? sorted : [{ id: "probe", name: "طالب", seat_number: "1" }];
        const probeWrap = document.createElement("div");
        probeWrap.style.width = "794px";
        probeWrap.style.background = "#ffffff";
        container.appendChild(probeWrap);
        const probeRoot = createRoot(probeWrap);
        await new Promise((resolve) => {
          probeRoot.render(
            <GradeSheetSheet school={settings} stage={ctx.stage} year={ctx.year}
                             grade={grade} cls={cls} subject={subj} maxScore={maxScore}
                             semester={semester} mode={mode} students={probeStudents}
                             valueMap={valueMap} fillType={fillType} components={components} />
          );
          document.fonts.ready.then(() => setTimeout(resolve, 150));
        });
        const probeNodeRect = probeWrap.getBoundingClientRect();
        const probeRowEls = Array.from(probeWrap.querySelectorAll("tbody tr"));
        const probeFooterEl = probeWrap.querySelector('[data-role="sheet-footer"]');
        // ارتفاع تذييل التوقيعات الحقيقي (مقاس فعليًا) + هامش أمان صغير (8px).
        const footerHeightPx = probeFooterEl
          ? probeFooterEl.getBoundingClientRect().height + 8
          : 60;
        // بداية جسم الجدول فعليًا (نهاية الترويسة + رأس الجدول) — من أعلى
        // أول صف حقيقي، وليس رقمًا مفروضًا.
        const headerBottomPx = probeRowEls.length
          ? probeRowEls[0].getBoundingClientRect().top - probeNodeRect.top
          : 0;
        const availableBudgetPx = A4_HEIGHT_PX - headerBottomPx - footerHeightPx;
        // ارتفاع كل صف بمفرده (فرق بين حافته السفلية وحافة الصف السابق —
        // وليس افتراض تساوي كل الصفوف).
        const rowHeights = probeRowEls.map((el, idx) => {
          const bottom = el.getBoundingClientRect().bottom - probeNodeRect.top;
          const prevBottom = idx === 0
            ? headerBottomPx
            : (probeRowEls[idx - 1].getBoundingClientRect().bottom - probeNodeRect.top);
          return bottom - prevBottom;
        });
        probeRoot.unmount();
        probeWrap.remove();

        // تعبئة تراكمية حقيقية: كل صفحة تُبنى بإضافة طلاب واحدًا تلو الآخر
        // بارتفاعهم الحقيقي، حتى تمتلئ ميزانية هذه الصفحة تحديدًا.
        const pageChunks = [];
        {
          let idx = 0;
          while (idx < sorted.length) {
            let used = 0;
            let count = 0;
            while (idx + count < sorted.length) {
              const h = rowHeights[idx + count] ?? rowHeights[rowHeights.length - 1] ?? 20;
              if (count > 0 && used + h > availableBudgetPx) break;
              used += h;
              count += 1;
            }
            count = Math.max(1, count); // ضمان تقدّم دائمًا حتى لو صف واحد أطول من الميزانية بالكامل
            pageChunks.push(sorted.slice(idx, idx + count));
            idx += count;
          }
        }
        if (!pageChunks.length) pageChunks.push([]); // لا طلاب — صفحة واحدة فارغة بدل تخطّي المادة صامتًا

        // تحقّق أمان أخير قبل التصدير: مجموع كل صفوف كل الصفحات لهذه
        // المادة يجب أن يطابق عدد الطلاب الأصلي تمامًا (0 مفقود، 0 مكرَّر).
        // لو حدث أي اختلاف (خطأ برمجي غير متوقَّع مستقبلًا)، نوقف التصدير
        // فورًا بدل تسليم ملف ناقص بصمت.
        const totalInChunks = pageChunks.reduce((sum, c) => sum + c.length, 0);
        if (totalInChunks !== sorted.length) {
          throw new Error(
            `خطأ داخلي في تقسيم الصفحات: ${sorted.length} طالبًا في الأصل مقابل ${totalInChunks} بعد التقسيم — تم إيقاف التصدير لمنع فقد بيانات.`
          );
        }

        let runningIndex = 0;
        for (let p = 0; p < pageChunks.length; p++) {
          const pageStudents = pageChunks[p];
          const wrap = document.createElement("div");
          // أبعاد صفحة A4 بالضبط (عند 96 نقطة/بوصة) — نفس الأسلوب المستخدَم
          // في الشهادات تمامًا، بدل بناء عنصر بارتفاع حر ثم "تحجيمه" لاحقًا.
          wrap.style.width = "794px";
          wrap.style.height = "1123px";
          wrap.style.overflow = "hidden";
          wrap.style.background = "#ffffff";
          container.appendChild(wrap);
          const root = createRoot(wrap);
          await new Promise((resolve) => {
            root.render(
              <GradeSheetSheet school={settings} stage={ctx.stage} year={ctx.year}
                               grade={grade} cls={cls} subject={subj} maxScore={maxScore}
                               semester={semester} mode={mode} students={pageStudents}
                               startIndex={runningIndex}
                               valueMap={valueMap} fillType={fillType} components={components} />
            );
            document.fonts.ready.then(() => setTimeout(resolve, 150));
          });
          runningIndex += pageStudents.length;
          nodes.push(wrap);
        }
      }

      const label = `${grade}-${cls === "__all__" ? "كل-الفصول" : cls}-${MODE_LABELS[mode]}-${SEMESTER_LABELS[semester]}`;
      if (nodes.length === 1) {
        await downloadCertificatePdf(nodes[0], `كشف-رصد-${label}.pdf`);
      } else {
        await downloadBulkCertificatesPdf(nodes, `كشوف-رصد-${label}.pdf`);
      }
      container.remove();
      toast.success("تم توليد كشف الرصد بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="gradesheet-page">
      <PageHeader
        icon={FileText}
        overline="التقارير والشهادات"
        title="طباعة كشوف رصد الدرجات"
        subtitle="توليد كشوف الرصد المفصّلة لكل فصل/مادة جاهزة للتوقيع والطباعة."
      />

      <div className="rounded-xl border border-border bg-white p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-[12px]">الفصل الدراسي</Label>
            <RadioGroup value={String(semester)} onValueChange={(v) => setSemester(Number(v))} className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="1" /> الفصل الأول</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="2" /> الفصل الثاني</label>
            </RadioGroup>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px]">نوع الرصد</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="flex gap-4">
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="year-work" /> أعمال السنة</label>
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="final-exam" /> الاختبار النهائي</label>
            </RadioGroup>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[12px]">حالة الكشف</Label>
          <RadioGroup value={fillType} onValueChange={setFillType} className="flex gap-4">
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="blank" /> كشف فارغ للرصد اليدوي</label>
            <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="filled" /> كشف بالدرجات المُدخَلة فعليًا</label>
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[12px]">الصف</Label>
            <Select value={grade} onValueChange={(v) => { setGrade(v); setCls(""); }}>
              <SelectTrigger className="h-10" data-testid="gs-grade"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
              <SelectContent>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px]">الفصل</Label>
            <Select value={cls} onValueChange={setCls} disabled={!grade}>
              <SelectTrigger className="h-10" data-testid="gs-class"><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">كل الفصول (مرتّب برقم الجلوس)</SelectItem>
                {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px]">المادة</Label>
            <Select value={subjectId} onValueChange={setSubjectId} disabled={!grade}>
              <SelectTrigger className="h-10" data-testid="gs-subject"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">كل المواد (ملف واحد مُجمَّع)</SelectItem>
                {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.subject_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={generate} disabled={!grade || !cls || generating} className="h-10 w-full" data-testid="gs-generate">
              <FileDown className="h-4 w-4 me-1.5" /> {generating ? "جارٍ التوليد…" : "تنزيل PDF"}
            </Button>
          </div>
        </div>
      </div>

      {(!grade || !cls) && (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف والفصل لعرض خيارات الطباعة
        </div>
      )}
    </div>
  );
}

/** ورقة كشف الرصد — قيمة خام واحدة فقط (بلا أي تحويل)، للطباعة/التصدير.
 * في وضع "أعمال السنة": عمود لكل مكوّن (بنفس ترتيب شاشة الرصد) ثم عمود
 * أخير "مجموع أعمال السنة" فقط (وليس أي مجموع نهائي للمادة). في وضع
 * "الاختبار النهائي": عمود واحد كما كان (المادة لا تحتوي مكوّنات هنا).
 * h-full + flex + mt-auto على تذييل التوقيعات: يجعله يلتصق دائمًا بأسفل
 * الصفحة (سواء ممتلئة بالكامل أو صفحة أخيرة جزئية بعدد أقل من الطلاب)،
 * بدل أن يظهر مباشرة بعد آخر صف بموضع متغيّر من صفحة لأخرى. */
function GradeSheetSheet({ school, stage, year, grade, cls, subject, maxScore, semester, mode, students, valueMap, fillType, components, startIndex = 0 }) {
  const isYearWork = mode === "year-work";
  const yearWorkTotal = (studentId) => {
    const row = valueMap[studentId] || {};
    return components.reduce((sum, c) => sum + (parseFloat(row[c.name]) || 0), 0);
  };
  return (
    <div className="mx-auto max-w-4xl h-full flex flex-col bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm">
      <div className="border-b-2 border-primary/50 pb-4 text-center space-y-1 relative">
        {school?.ministry_logo && (
          <img src={school.ministry_logo} alt="شعار الوزارة"
               className="absolute top-0 start-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار المدرسة"
               className="absolute top-0 end-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        <div className="text-primary font-display text-xl font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
        {school?.education_dept && <div className="text-primary font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-primary font-medium">{school.office_name}</div>}
        <div className="text-primary font-display text-lg font-bold">{school?.school_name || "المدرسة"}</div>
      </div>

      <div className="mt-6 text-center">
        <h2 className="font-display text-lg font-bold text-primary">
          كشف رصد درجات — {MODE_LABELS[mode]}
        </h2>
        <div className="mt-2 text-sm">
          {stage} — الصف: {grade} — الفصل: {cls === "__all__" ? "كل الفصول" : cls} — {SEMESTER_LABELS[semester]}
        </div>
        <div className="mt-1 text-sm font-semibold">
          المادة: {subject?.subject_name}
          {!isYearWork && ` — النهاية العظمى: ${maxScore ?? "—"}`}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">العام الدراسي: {year}</div>
      </div>

      {/* رؤوس الأعمدة بحجمها الأصلي الكامل (text-sm) دون أي تصغير — التصغير
          يقتصر على <tbody> فقط أدناه، بحيث يستوعب جسم الجدول أكبر عدد ممكن
          من الطلاب داخل نفس مساحة A4 الثابتة، بدون المساس بمقروئية العنوان
          أو رؤوس الأعمدة. عرض عمودَي "رقم الجلوس" و"مجموع أعمال السنة"
          قُلِّل، فيحصل عمود "اسم الطالب" (بلا عرض ثابت، يستهلك المساحة
          المتبقية تلقائيًا) على مساحة أكبر.
          ملاحظة: صفوف "الاختبار النهائي" (يدوي أو بدرجات مُدخَلة) أوسع
          ارتفاعًا (padding مضاعَف تقريبًا) عن صفوف "أعمال السنة" — لأن عدد
          أعمدتها أقل بكثير (عمود درجة واحد فقط)، فلا حاجة لضغطها بنفس درجة
          ضغط أعمال السنة (التي تحتاج مساحة أكبر لعدد أعمدة المكوّنات). حساب
          سعة الصفحة (rowsPerPage) في generate() يقيس هذا تلقائيًا من نفس
          الصفوف الحقيقية، فيتكيّف تلقائيًا مع الفرق دون أي تعديل إضافي. */}
      <table className="w-full mt-6 border-collapse" style={{ tableLayout: "fixed" }}>
        <thead className="text-sm">
          <tr className="bg-primary text-white">
            <th className="px-1 py-2 text-center border border-primary w-8">م</th>
            <th className="px-1 py-2 text-center border border-primary w-14">رقم الجلوس</th>
            <th className="px-2 py-2 text-start border border-primary">اسم الطالب</th>
            {isYearWork ? (
              <>
                {components.map((c) => (
                  <th key={c.name} className="px-2 py-2 text-center border border-primary">
                    {c.name} (من {c.max_score})
                  </th>
                ))}
                <th className="px-1 py-2 text-center border border-primary w-16 bg-primary/80">مجموع أعمال السنة</th>
              </>
            ) : (
              <th className="px-2 py-2 text-center border border-primary w-32">الدرجة</th>
            )}
          </tr>
        </thead>
        <tbody className={isYearWork ? "text-[10px] leading-tight" : "text-xs leading-normal"}>
          {students.map((s, i) => (
            <tr key={s.id || i}>
              <td className={`${isYearWork ? "px-1 py-0.5" : "px-1 py-2.5"} border border-slate-200 text-center`}>{startIndex + i + 1}</td>
              <td className={`${isYearWork ? "px-1 py-0.5" : "px-1 py-2.5"} border border-slate-200 text-center`}>{s.seat_number || "—"}</td>
              <td className={`${isYearWork ? "px-1.5 py-0.5" : "px-1.5 py-2.5"} border border-slate-200`}>{s.name}</td>
              {isYearWork ? (
                <>
                  {components.map((c) => (
                    <td key={c.name} className="px-1 py-0.5 border border-slate-200 text-center">
                      {fillType === "filled" ? ((valueMap[s.id] || {})[c.name] ?? "—") : ""}
                    </td>
                  ))}
                  <td className="px-1 py-0.5 border border-slate-200 text-center font-bold bg-primary/5">
                    {fillType === "filled" ? yearWorkTotal(s.id) : ""}
                  </td>
                </>
              ) : (
                <td className="px-1.5 py-2.5 border border-slate-200 text-center">
                  {fillType === "filled" ? (valueMap[s.id] ?? "—") : ""}
                </td>
              )}
            </tr>
          ))}
          {students.length === 0 && (
            <tr><td colSpan={isYearWork ? 3 + components.length + 1 : 4} className="px-2 py-6 border border-slate-200 text-center text-muted-foreground text-sm">
              لا يوجد طلاب في هذا التحديد
            </td></tr>
          )}
        </tbody>
      </table>

      {/* تذييل التوقيعات — يظهر أسفل كل صفحة من صفحات الكشف (mt-auto يدفعه
          لأسفل الصفحة دائمًا، بصرف النظر عن عدد صفوف هذه الصفحة تحديدًا).
          data-role يُستخدَم فقط لقياس ارتفاعه الفعلي عند حساب سعة الصفحة
          (انظر generate() أعلاه) — لا أثر له على الشكل. */}
      <div data-role="sheet-footer" className="mt-auto pt-6 grid grid-cols-3 gap-4 text-sm">
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="font-semibold text-primary">رصده:</span>
          <span className="flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
        </div>
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="font-semibold text-primary">أملاه:</span>
          <span className="flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
        </div>
        <div className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="font-semibold text-primary">راجعه:</span>
          <span className="flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
        </div>
      </div>
    </div>
  );
}
